import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Copy, Check } from 'lucide-react';

interface JsonViewerProps {
  data: any;
  initialExpanded?: boolean;
}

const JsonViewer: React.FC<JsonViewerProps> = ({ data, initialExpanded = true }) => {

  if (data === null) return <span className="text-rose-500 font-mono">null</span>;
  if (data === undefined) return <span className="text-gray-400 font-mono">undefined</span>;

  if (typeof data !== 'object') {
    let colorClass = 'text-gray-800';
    if (typeof data === 'string') colorClass = 'text-green-600';
    if (typeof data === 'number') colorClass = 'text-yellow-600';
    if (typeof data === 'boolean') colorClass = 'text-purple-600';
    
    const displayValue = typeof data === 'string' ? `"${data}"` : String(data);
    
    return <span className={`font-mono ${colorClass}`}>{displayValue}</span>;
  }

  const isArray = Array.isArray(data);
  const isEmpty = Object.keys(data).length === 0;
  
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [isExpanded, setIsExpanded] = useState(initialExpanded);

  if (isEmpty) {
    return <span className="text-gray-600 font-mono">{isArray ? '[]' : '{}'}</span>;
  }

  return (
    <div className="font-mono text-xs">
       {/* Root copy button only for top level? Or we can rely on parent container */}
      <div className="flex items-start">
        <button 
          onClick={() => setIsExpanded(!isExpanded)} 
          className="p-0.5 mr-1 hover:bg-gray-100 rounded text-gray-500"
        >
          {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
        
        <div className="flex-1">
            {!isExpanded ? (
                <span className="text-gray-600 cursor-pointer" onClick={() => setIsExpanded(true)}>
                    {isArray ? `Array(${data.length})` : '{...}'}
                </span>
            ) : (
                <div>
                     <span className="text-gray-500">{isArray ? '[' : '{'}</span>
                     <div className="pl-4 border-l-2 border-gray-100 ml-1">
                        {Object.entries(data).map(([key, value], index, arr) => (
                            <div key={key} className="my-0.5">
                                {!isArray && <span className="text-blue-600 mr-1">"{key}":</span>}
                                <JsonViewer data={value} initialExpanded={true} />
                                {index < arr.length - 1 && <span className="text-gray-400">,</span>}
                            </div>
                        ))}
                     </div>
                     <span className="text-gray-500">{isArray ? ']' : '}'}</span>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

export const JsonViewerContainer: React.FC<{ data: any; title?: string }> = ({ data, title }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
  
    return (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
            {(title || true) && (
                <div className="px-3 py-2 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700 uppercase">{title || 'JSON'}</span>
                    <button 
                        onClick={handleCopy}
                        className="p-1 hover:bg-gray-200 rounded text-gray-500 transition-colors"
                        title="Copy JSON"
                    >
                        {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                </div>
            )}
            <div className="p-3 overflow-auto max-h-[500px]">
                <JsonViewer data={data} initialExpanded={true} />
            </div>
        </div>
    );
}

export default JsonViewerContainer;
